import { Box } from '@mui/material';
import { styled } from '@mui/material';

interface SidebarContainerProps {
  expanded?: boolean;
}

export const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'expanded' &&
    prop !== 'iconColumnBackgroundColor' &&
    prop !== 'expandedBackgroundColor' &&
    prop !== 'backgroundColor' &&
    prop !== 'boxShadow',
})<SidebarContainerProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  transition: theme.transitions.create(
    ['width', 'background-color', 'border-color'],
    {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    },
  ),
  overflow: 'hidden',
  height: '100%',
  // Use MUI theme palette for colors
  border: `1px solid ${theme.palette.sidebar.border}`,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0px 4px 8px rgba(0, 0, 0, 0.15)'
      : '0px 4px 8px rgba(0, 0, 0, 0.1)',
  // Border radius is always 16px - individual sections handle their own corners
  borderRadius: '16px',
}));

export const SidebarSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(5),
  width: '100%',
}));

export const SidebarIconButton = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: '30px',
  transition: theme.transitions.create(['background-color', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  '& svg': {
    color: theme.palette.sidebar.iconColor,
  },
  '&.active': {
    backgroundColor: theme.palette.sidebar.iconActive,
    '& svg': {
      color: theme.palette.sidebar.iconActiveText,
    },
  },
  // Only for Pencil icon: override fill/stroke in active state
  '&.active.pencil-active svg *': {
    stroke: `${theme.palette.sidebar.iconActiveText} !important`,
    fill: `${theme.palette.sidebar.iconActiveText} !important`,
  },
  // For Heart icon: ensure fill matches the outline color

  '&.active.heart-icon svg *': {
    fill: `${theme.palette.sidebar.iconActiveText} !important`,
  },
}));

export const SidebarIconButtonAccent = styled(SidebarIconButton)(
  ({ theme }) => ({
    backgroundColor: theme.palette.sidebar.accentBg,
    position: 'relative',
    '& svg': {
      color: theme.palette.sidebar.accentIcon,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      background:
        theme.palette.mode === 'dark'
          ? 'transparent'
          : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
      opacity: 0.1,
    },
    '&:hover': {
      backgroundColor: theme.palette.sidebar.accentBgHover,
      transform: 'scale(1.05)',
    },
  }),
);

interface SidebarContentProps {
  expanded?: boolean;
}

export const SidebarContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<SidebarContentProps>(({ theme, expanded }) => ({
  position: 'relative',
  flex: 1,
  opacity: expanded ? 1 : 0,
  visibility: expanded ? 'visible' : 'hidden',
  transition: theme.transitions.create(
    ['opacity', 'padding', 'backgroundColor'],
    {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    },
  ),
  transitionDelay: expanded ? '0.1s' : '0s',
  // Changed from 'visible' to 'hidden' to prevent content from expanding beyond sidebar bounds
  // This is a better default and prevents flex container overflow issues
  overflow: 'hidden',
  minWidth: 0, // Allow flex items to shrink below their content size
}));
